import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import Chart from 'chart.js/auto';
import { EMPTY, Subject, catchError, map, switchMap, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { VscKpiCategoryService } from '../../services/vsc-kpi-category.service';
import { FinalScoreKpiData } from '../../models/vsc-scorecard.model';
import { parseMonthLabel } from './vsc-scorecard.mapper';
import { buildThemeObserver, createTooltipElement, destroyChartParts, ScoreBand, SCORE_COLOR_TOKENS } from '../../shared-components/chart-utils';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_DISPLAY = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Returns the 8 current-year periods ending at [month, year], plus the matching 8 prior-year periods. */
function buildPeriodList(month: string, year: number): { month: string; year: number }[] {
  const idx = MONTHS.indexOf(month);
  const curr: { month: string; year: number }[] = [];
  for (let i = 7; i >= 0; i--) {
    let m = idx - i;
    let y = year;
    if (m < 0) {
      m += 12;
      y--;
    }
    curr.push({ month: MONTHS[m], year: y });
  }
  return [...curr, ...curr.map((p) => ({ month: p.month, year: p.year - 1 }))];
}

@Component({
  selector: 'app-overall-performance-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './overall-performance-card.component.html',
  styleUrl: './overall-performance-card.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverallPerformanceCardComponent implements OnDestroy {
  private readonly service = inject(VscKpiCategoryService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Inputs
  readonly vendorCode = input.required<string>();
  readonly selectedMonthLabel = input.required<string>(); // "Aug 2026" (from detail view)
  readonly isShopView = input(false);

  // Parent reads the calculatedAt of the selected month to populate "Scores Compiled on"
  readonly compiledAt = output<string>();

  readonly isLoading = signal(true);
  readonly hasError = signal(false);
  readonly currentYearData = signal<FinalScoreKpiData[]>([]);
  readonly priorYearData = signal<FinalScoreKpiData[]>([]);

  private chart: Chart | null = null;
  private tooltipEl: HTMLElement | null = null;
  private canvasEl: HTMLCanvasElement | null = null;
  private themeObserver: MutationObserver | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly loadTrigger$ = new Subject<{ vendorCode: string; month: string; year: number }>();

  @ViewChild('overallChart')
  set chartCanvasRef(el: ElementRef<HTMLCanvasElement> | undefined) {
    if (el) {
      this.canvasEl = el.nativeElement;
      setTimeout(() => this.buildChart(this.canvasEl!), 0);
    } else {
      this.canvasEl = null;
      this.destroyChart();
    }
  }

  readonly parsedMonth = computed(() => parseMonthLabel(this.selectedMonthLabel()));

  readonly selectedEntry = computed(() => {
    const { month, year } = this.parsedMonth();
    const data = this.currentYearData();
    return data.find((d) => d.month === month && d.year === year) ?? (data.length > 0 ? data.at(-1)! : null);
  });

  readonly displayScore = computed(() => {
    const e = this.selectedEntry();
    if (!e) return 0;
    return this.isShopView() ? e.outOfFiveScore : e.outOfHundredScore;
  });

  readonly displayMax = computed(() => (this.isShopView() ? 5 : 100));

  readonly progressWidth = computed(() => `${Math.min(100, (this.displayScore() / this.displayMax()) * 100)}%`);

  readonly scoreBand = computed((): ScoreBand => {
    const s = this.selectedEntry()?.outOfFiveScore ?? 0;
    if (s < 2) return 'poor';
    if (s < 3) return 'average';
    if (s <= 4.5) return 'good';
    return 'excellent';
  });

  readonly scoreClass = computed(() => `score--${this.scoreBand()}`);

  readonly trendDelta = computed(() => {
    const data = this.currentYearData();
    if (data.length < 2) return null;
    const getValue = (d: FinalScoreKpiData) => (this.isShopView() ? d.outOfFiveScore : d.outOfHundredScore);
    return +(getValue(data.at(-1)!) - getValue(data[0])).toFixed(2);
  });

  readonly trendLabel = computed(() => {
    const d = this.trendDelta();
    if (d === null) return '';
    return `${d >= 0 ? '↑' : '↓'} ${String(Math.abs(d))} pts`;
  });

  readonly trendIsPositive = computed(() => (this.trendDelta() ?? 0) >= 0);

  constructor() {
    this.setupThemeObserver();
    this.loadTrigger$
      .pipe(
        switchMap((req) => {
          this.isLoading.set(true);
          this.hasError.set(false);
          this.cdr.markForCheck();
          return this.service.postFinalScore({ vendorCode: req.vendorCode, period: buildPeriodList(req.month, req.year) }).pipe(
            map((response) => ({ response, req })),
            catchError(() => {
              this.hasError.set(true);
              this.isLoading.set(false);
              this.cdr.markForCheck();
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ response, req }) => this.processResponse(response.kpiData, req.month, req.year));

    effect(() => {
      const { month, year } = this.parsedMonth();
      const code = this.vendorCode();
      if (code) {
        this.loadTrigger$.next({ vendorCode: code, month, year });
      }
    });

    effect(() => {
      this.isShopView(); // track toggle
      setTimeout(() => {
        if (this.canvasEl) this.buildChart(this.canvasEl);
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.themeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyChart();
  }

  private processResponse(kpiData: FinalScoreKpiData[], selectedMonth: string, selectedYear: number): void {
    const idx = MONTHS.indexOf(selectedMonth);
    const currPeriods: { month: string; year: number }[] = [];
    for (let i = 7; i >= 0; i--) {
      let m = idx - i;
      let y = selectedYear;
      if (m < 0) {
        m += 12;
        y--;
      }
      currPeriods.push({ month: MONTHS[m], year: y });
    }
    const priorPeriods = currPeriods.map((p) => ({ month: p.month, year: p.year - 1 }));

    const dataMap = new Map(kpiData.map((d) => [`${d.month}-${d.year}`, d]));

    this.currentYearData.set(currPeriods.map((p) => dataMap.get(`${p.month}-${p.year}`)).filter(Boolean) as FinalScoreKpiData[]);
    this.priorYearData.set(priorPeriods.map((p) => dataMap.get(`${p.month}-${p.year}`)).filter(Boolean) as FinalScoreKpiData[]);

    const currList = this.currentYearData();
    const selected = dataMap.get(`${selectedMonth}-${selectedYear}`) ?? (currList.length > 0 ? currList.at(-1) : undefined);
    if (selected?.calculatedAt) {
      this.compiledAt.emit(selected.calculatedAt);
    }

    this.isLoading.set(false);
    this.cdr.markForCheck();
  }

  private buildChart(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.destroyChart();

    const container = canvas.parentElement ?? document.body;
    container.style.position = 'relative';

    const style = getComputedStyle(document.documentElement);
    const cs = getComputedStyle(container);
    const band = this.scoreBand();
    const lineColor = style.getPropertyValue(SCORE_COLOR_TOKENS[band]).trim();
    const priorColor = cs.getPropertyValue('--overall-prior-color').trim();
    const tooltipShadow = cs.getPropertyValue('--overall-tooltip-shadow').trim();
    const labelColor = style.getPropertyValue('--mds_brand_appearance_neutral_default_text-color').trim();

    const isShopView = this.isShopView();
    const currData = this.currentYearData();
    const priorData = this.priorYearData();
    const { month, year } = this.parsedMonth();

    // Build aligned 8-slot arrays — null where data is absent
    const idx = MONTHS.indexOf(month);
    const xSlots: { month: string; year: number; label: string }[] = [];
    for (let i = 7; i >= 0; i--) {
      let m = idx - i;
      let y = year;
      if (m < 0) {
        m += 12;
        y--;
      }
      xSlots.push({ month: MONTHS[m < 0 ? m + 12 : m], year: y, label: MONTH_DISPLAY[m < 0 ? m + 12 : m] });
    }

    const currMap = new Map(currData.map((d) => [`${d.month}-${d.year}`, d]));
    const priorMap = new Map(priorData.map((d) => [`${d.month}-${d.year}`, d]));

    const getValue = (d: FinalScoreKpiData) => (isShopView ? d.outOfFiveScore : d.outOfHundredScore);

    const currScores = xSlots.map((s) => {
      const d = currMap.get(`${s.month}-${s.year}`);
      return d ? getValue(d) : null;
    });
    const priorScores = xSlots.map((s) => {
      const d = priorMap.get(`${s.month}-${s.year - 1}`);
      return d ? getValue(d) : null;
    });

    const allValues = [...currScores, ...priorScores].filter((v): v is number => v !== null);
    const dataMin = allValues.length ? Math.min(...allValues) : 0;
    const defaultMax = isShopView ? 5 : 100;
    const dataMax = allValues.length ? Math.max(...allValues) : defaultMax;
    const range = Math.max(dataMax - dataMin, 1);
    const yMin = Math.max(0, dataMin - range * 0.25);
    const yMax = dataMax + range * 0.5; // extra top room for data labels
    const monthLabels = xSlots.map((s) => s.label);
    const unit = isShopView ? '/5' : '%';

    const dataLabelPlugin = {
      id: 'overallDataLabels',
      afterDatasetsDraw: (chart: Chart) => {
        const meta0 = chart.getDatasetMeta(0);
        const meta1 = chart.getDatasetMeta(1);
        chart.ctx.save();
        chart.ctx.font = '11px sans-serif';
        chart.ctx.textAlign = 'center';
        chart.ctx.fillStyle = labelColor;
        meta0.data.forEach((point, i) => {
          const score = currScores[i];
          if (score === null) return;
          const otherY = meta1.data[i]?.y ?? point.y;
          const above = otherY >= point.y;
          const baseY = above ? point.y - 20 : point.y + 16;
          chart.ctx.fillText(monthLabels[i], point.x, baseY);
          chart.ctx.fillText(`(${String(score)})`, point.x, baseY + 12);
        });
        chart.ctx.restore();
      },
    };

    const crosshairPlugin = {
      id: 'overallCrosshair',
      afterDraw: (chart: Chart) => {
        const active = chart.getActiveElements();
        if (!active.length) return;
        const x = active[0].element.x;
        const ys = active.map((el) => el.element.y);
        chart.ctx.save();
        chart.ctx.beginPath();
        chart.ctx.setLineDash([4, 4]);
        chart.ctx.strokeStyle = priorColor;
        chart.ctx.lineWidth = 1;
        chart.ctx.moveTo(x, Math.min(...ys) - 8);
        chart.ctx.lineTo(x, Math.max(...ys) + 8);
        chart.ctx.stroke();
        chart.ctx.restore();
      },
    };

    const getTooltipEl = (): HTMLElement => {
      this.tooltipEl ??= createTooltipElement(container, tooltipShadow, 'overall-trend-tooltip');
      return this.tooltipEl;
    };

    const externalTooltip = (context: { chart: Chart; tooltip: any }) => {
      const { chart, tooltip } = context;
      const el = getTooltipEl();
      if (tooltip.opacity === 0) {
        el.style.opacity = '0';
        return;
      }

      const monthLabel = (tooltip.title ?? [])[0] ?? '';
      const points: any[] = tooltip.dataPoints ?? [];
      let html = `<div style="font-weight:700;font-size:12px;margin-bottom:4px;">${monthLabel}</div>
        <hr style="border:none;border-top:1px solid var(--mds_brand_appearance_neutral_default_border-color);margin:0 0 6px;">`;

      points.forEach((item: any, i: number) => {
        const label = item.datasetIndex === 0 ? 'Current Year' : 'Prior Year';
        const val = item.formattedValue;
        if (i > 0)
          html += `<hr style="border:none;border-top:1px solid var(--mds_brand_appearance_neutral_default_border-color);margin:6px 0;">`;
        html += `<div style="display:flex;justify-content:space-between;gap:16px;">
          <span style="color:var(--mds_brand_appearance_neutral_weak_text-color)">${label}</span>
          <span style="font-weight:500">${val}${unit}</span>
        </div>`;
      });

      el.innerHTML = html;
      const tw = el.offsetWidth || 190;
      const xPos = tooltip.caretX + tw + 20 > chart.width ? tooltip.caretX - tw - 10 : tooltip.caretX + 10;
      el.style.left = `${xPos}px`;
      el.style.top = `${Math.max(0, tooltip.caretY - 20)}px`;
      el.style.opacity = '1';
    };

    this.chart = new Chart(ctx, {
      type: 'line',
      plugins: [dataLabelPlugin, crosshairPlugin],
      data: {
        labels: monthLabels,
        datasets: [
          {
            label: 'Current Year',
            data: currScores,
            borderColor: lineColor,
            backgroundColor: lineColor,
            pointBackgroundColor: lineColor,
            tension: 0.3,
            pointRadius: 4,
            clip: false,
          },
          {
            label: 'Prior Year',
            data: priorScores,
            borderColor: priorColor,
            backgroundColor: priorColor,
            pointBackgroundColor: priorColor,
            borderDash: [6, 4],
            tension: 0.3,
            pointRadius: 3,
            clip: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 40, left: 20, right: 20, bottom: 36 } },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false, external: externalTooltip },
        },
        scales: {
          x: { display: false },
          y: { display: false, min: yMin, max: yMax },
        },
      },
    });
  }

  private destroyChart(): void {
    destroyChartParts(this.chart, this.tooltipEl);
    this.chart = null;
    this.tooltipEl = null;
  }

  private setupThemeObserver(): void {
    this.themeObserver = buildThemeObserver(() => {
      if (this.canvasEl) this.buildChart(this.canvasEl);
    });
  }
}
